import { useCallback, useRef, useSyncExternalStore } from 'react';
import { isFunction, isNil } from 'lodash/fp';

function dispatchStorageEvent(key, newValue) {
    window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
}

function setLocalStorageItem(key, value) {
    const stringifiedValue = JSON.stringify(value);
    window.localStorage.setItem(key, stringifiedValue);
    dispatchStorageEvent(key, stringifiedValue);
}

function getLocalStorageItem(key) {
    return window.localStorage.getItem(key);
}

function removeLocalStorageItem(key) {
    window.localStorage.removeItem(key);
    dispatchStorageEvent(key, null);
}

function subscribe(callback) {
    window.addEventListener('storage', callback);
    return () => window.removeEventListener('storage', callback);
}

export function useLocalStorage(key, initialValue) {
    const stringifiedInitialValue = JSON.stringify(initialValue);
    const getSnapshot = useCallback(
        () => getLocalStorageItem(key) ?? stringifiedInitialValue,
        [key, stringifiedInitialValue]
    );

    const store = useRef();
    store.current = JSON.parse(useSyncExternalStore(subscribe, getSnapshot));

    const setState = useCallback(
        (value) => {
            try {
                const nextState = isFunction(value) ? value(store.current) : value;
                if (isNil(nextState)) {
                    removeLocalStorageItem(key);
                } else {
                    setLocalStorageItem(key, nextState);
                }
            } catch (e) {
                console.warn(e);
            }
        },
        [key]
    );

    return [store.current, setState];
}
